'use client';

import Button from '@ui/Button';
import SafeImage from '@ui/SafeImage';
import { getCDNUrl } from '@util/index';
import { useAuth } from 'context/AuthContext';
import { AnimatePresence } from 'framer-motion';
import { chunk } from 'lodash';
import { MotionDiv } from 'motion';
import Link from 'next/link';
import { useParams } from 'next/navigation';
import { useRef, useState } from 'react';
import NavMenu from './NavMenu';
import NavMenuSection from './NavMenuSection';
import { staticCategories } from './utils/categories';

export default function CategoryBar() {
  const { userDoc } = useAuth();
  const params = useParams();

  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };

  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(() => setActiveIndex(null), 120);
  };

  const closeMenu = () => {
    cancelClose();
    setActiveIndex(null);
  };

  const isCategoryActive = (category: string) => {
    const allCategories = params?.categorySlug;
    return allCategories?.includes(category);
  };

  const visibleCategories = staticCategories.filter(
    (cat) => !cat.excludeFromNav,
  );
  const active = activeIndex !== null ? visibleCategories[activeIndex] : null;

  const getDisplayLabel = (label: string) => {
    if (userDoc && label === 'Bike Parts') return 'Dirt Bike Parts';
    return label;
  };

  const handleTopLevelClick = (e: React.MouseEvent) => {
    if (active?.disableTopLevelLink) {
      e.preventDefault();
      return;
    }
    closeMenu();
  };

  return (
    <div
      className="relative hidden lg:block"
      onMouseLeave={scheduleClose}
      onMouseEnter={cancelClose}
    >
      <div className="z-50 mx-auto flex w-full max-w-content items-center justify-between gap-[1.6rem] overflow-hidden bg-brand-white text-left font-medium text-black/[0.87] [&>*]:text-[1.4rem]">
        <div className="flex gap-x-[2.4rem] space-x-8 whitespace-nowrap p-0 [&>*]:text-left [&>*]:uppercase">
          {visibleCategories.map((category, index) => (
            <NavMenu
              key={category.label}
              label={category.label}
              displayLabel={getDisplayLabel(category.label)}
              href={category.href}
              image={category.image}
              disableTopLevelLink={category.disableTopLevelLink}
              onHover={() => {
                cancelClose();
                setActiveIndex(index);
              }}
              onLinkClick={closeMenu}
            />
          ))}
        </div>

        <div className="flex items-center space-x-4">
          <Button
            text="ON SALE"
            href="/shop/sale"
            type="text"
            noXPadding
            width="small"
            leadingIcon={<SaleTag />}
          />
        </div>
      </div>

      <AnimatePresence>
        {active && (
          <MotionDiv
            key="panel"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="absolute left-0 top-full z-40 mt-4 w-full"
            onMouseEnter={cancelClose}
            onMouseLeave={scheduleClose}
          >
            <div
              className="mx-auto max-h-[calc(100vh_-_13rem)] w-full max-w-content overflow-hidden overflow-y-auto rounded-[2rem] border-t-4 bg-white"
              style={{ border: `4px solid ${active.color}` }}
            >
              <AnimatePresence mode="popLayout" initial={false}>
                <MotionDiv
                  key={activeIndex}
                  initial={{ opacity: 0, x: 16 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -16 }}
                  transition={{ duration: 0.22, ease: [0.22, 1, 0.36, 1] }}
                  className="flex gap-x-4"
                >
                  <Link
                    href={active.href}
                    className="flex h-auto w-[40rem] shrink-0 flex-col justify-between"
                    style={{ backgroundColor: active.color }}
                    onClick={handleTopLevelClick}
                  >
                    <div className="flex w-full justify-center pt-16 text-center">
                      <span className="text-[2rem] font-semibold capitalize text-white">
                        Shop {active.label}
                      </span>
                    </div>

                    <div className="bottom-0 left-0 flex overflow-hidden">
                      <SafeImage
                        src={getCDNUrl(active.image ?? '')}
                        className={`h-[30rem] w-full object-cover ${
                          active.label === 'PWC' ? 'scale-[1.35]' : ''
                        }`}
                        alt={active.label + ' large category image'}
                      />
                    </div>
                  </Link>

                  <div className="flex w-full flex-wrap gap-y-16 py-8">
                    {active.sections?.map((section) => {
                      const linkGroups = chunk(section.links ?? [], 10);
                      return (
                        <NavMenuSection
                          key={section.label}
                          label={section.label}
                          href={section.href}
                          onClick={closeMenu}
                        >
                          <div className="flex gap-[1.6rem]">
                            {linkGroups.map((group) => (
                              <div
                                className="flex flex-col gap-y-4 pt-4"
                                key={group.length.toString()}
                              >
                                {group.map((link) => (
                                  <Link
                                    key={link.label}
                                    className={`font-light capitalize ${
                                      isCategoryActive(link.label.toLowerCase())
                                        ? 'text-brand-secondary'
                                        : 'text-black hover:text-brand-secondary'
                                    }`}
                                    href={link.href}
                                    onClick={closeMenu}
                                  >
                                    {link.label}
                                  </Link>
                                ))}
                              </div>
                            ))}
                          </div>
                        </NavMenuSection>
                      );
                    })}
                  </div>
                </MotionDiv>
              </AnimatePresence>
            </div>
          </MotionDiv>
        )}
      </AnimatePresence>

      {active && (
        <div className="fixed left-0 top-[14rem] z-0 h-screen w-screen bg-black/50" />
      )}
    </div>
  );
}

const SaleTag = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <title>Sale Tag</title>
    <path
      d="M3.66668 4.66659C3.40146 4.66659 3.14711 4.56123 2.95957 4.37369C2.77203 4.18616 2.66668 3.9318 2.66668 3.66659C2.66668 3.40137 2.77203 3.14701 2.95957 2.95948C3.14711 2.77194 3.40146 2.66659 3.66668 2.66659C3.93189 2.66659 4.18625 2.77194 4.37378 2.95948C4.56132 3.14701 4.66668 3.40137 4.66668 3.66659C4.66668 3.9318 4.56132 4.18616 4.37378 4.37369C4.18625 4.56123 3.93189 4.66659 3.66668 4.66659ZM14.2733 7.71992L8.27334 1.71992C8.03334 1.47992 7.70001 1.33325 7.33334 1.33325H2.66668C1.92668 1.33325 1.33334 1.92659 1.33334 2.66659V7.33325C1.33334 7.69992 1.48001 8.03325 1.72668 8.27325L7.72001 14.2733C7.96668 14.5133 8.30001 14.6666 8.66668 14.6666C9.03334 14.6666 9.36668 14.5133 9.60668 14.2733L14.2733 9.60659C14.52 9.36659 14.6667 9.03325 14.6667 8.66659C14.6667 8.29325 14.5133 7.95992 14.2733 7.71992Z"
      fill="#C41719"
    />
  </svg>
);
